import React, {
  forwardRef,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link, useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import { publicationsAPI } from '../services/api';
import './PublicationReader.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs?v=4.10.38';

const HTMLFlipBook = lazy(() => import('react-pageflip'));

const DEFAULT_PAGE_ASPECT_RATIO = 3 / 4;
const BOOK_PAGE_HEIGHT = 920;
const BOOK_PAGE_MIN_HEIGHT = 400;
const BOOK_PAGE_MAX_HEIGHT = 1040;
const BOOK_PAGE_ENGINE_MAX_HEIGHT = 2400;
const FULLSCREEN_TOOLBAR_HEIGHT = 58;
const FULLSCREEN_PADDING = 32;

const PdfCanvasPage = ({ document, pageNumber, shouldRender, lazy = false }) => {
  const shellRef = useRef(null);
  const canvasRef = useRef(null);
  const [nearViewport, setNearViewport] = useState(!lazy);
  const [rendered, setRendered] = useState(false);
  const [renderWidth, setRenderWidth] = useState(0);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;

    const updateRenderWidth = () => {
      const nextWidth = Math.round(shell.getBoundingClientRect().width);
      if (nextWidth > 0) setRenderWidth(nextWidth);
    };

    updateRenderWidth();

    if (!('ResizeObserver' in window)) {
      window.addEventListener('resize', updateRenderWidth);
      return () => window.removeEventListener('resize', updateRenderWidth);
    }

    const observer = new ResizeObserver(updateRenderWidth);
    observer.observe(shell);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!lazy || !shellRef.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(shellRef.current);
    return () => observer.disconnect();
  }, [lazy]);

  useEffect(() => {
    let cancelled = false;
    let renderTask;
    const canvas = canvasRef.current;
    const canRender = document && shouldRender && nearViewport && canvas;

    if (!canRender) {
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
      }
      setRendered(false);
      return undefined;
    }

    const renderPage = async () => {
      try {
        const page = await document.getPage(pageNumber);
        if (cancelled) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const targetWidth = renderWidth || shellRef.current?.clientWidth || baseViewport.width;
        const renderScale = Math.min(
          4,
          Math.max(1.35, (targetWidth / baseViewport.width) * pixelRatio)
        );
        const viewport = page.getViewport({ scale: renderScale });
        const context = canvas.getContext('2d', { alpha: false });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.aspectRatio = `${viewport.width} / ${viewport.height}`;
        renderTask = page.render({ canvasContext: context, viewport });
        await renderTask.promise;
        if (!cancelled) setRendered(true);
      } catch (error) {
        if (error?.name !== 'RenderingCancelledException') {
          console.error(`Failed to render page ${pageNumber}:`, error);
        }
      }
    };

    renderPage();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [document, pageNumber, shouldRender, nearViewport, renderWidth]);

  return (
    <div className="publication-pdf-page-shell" ref={shellRef}>
      {!rendered && <div className="publication-page-loading">Page {pageNumber}</div>}
      <canvas
        ref={canvasRef}
        className={rendered ? 'loaded' : ''}
        aria-label={`Page ${pageNumber}`}
      />
    </div>
  );
};

const FlipPage = forwardRef(({ document, pageNumber, shouldRender }, ref) => (
  <div className="publication-flip-page" ref={ref}>
    <PdfCanvasPage
      document={document}
      pageNumber={pageNumber}
      shouldRender={shouldRender}
    />
  </div>
));

const ScrollingPdfPage = ({ document, pageNumber }) => {
  const pageRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!pageRef.current || !('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisible(true);
        observer.disconnect();
      },
      { rootMargin: '160px 0px', threshold: 0.08 }
    );
    observer.observe(pageRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      id={`publication-page-${pageNumber}`}
      className={`publication-scroll-page ${visible ? 'is-visible' : ''}`}
      data-page-number={pageNumber}
      ref={pageRef}
    >
      <span className="publication-scroll-page-number">{pageNumber}</span>
      <PdfCanvasPage
        document={document}
        pageNumber={pageNumber}
        shouldRender
        lazy
      />
    </div>
  );
};

const PublicationImage = ({ slug, pageNumber, title, shouldRender, lazy = false }) => {
  const shellRef = useRef(null);
  const [nearViewport, setNearViewport] = useState(!lazy);
  const [imageUrl, setImageUrl] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [imageError, setImageError] = useState('');

  useEffect(() => {
    if (!lazy || !shellRef.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(shellRef.current);
    return () => observer.disconnect();
  }, [lazy]);

  useEffect(() => {
    if (!shouldRender || !nearViewport) return undefined;
    let active = true;
    let objectUrl;
    setImageUrl('');
    setLoaded(false);
    setImageError('');

    publicationsAPI.getPage(slug, pageNumber)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      })
      .catch((error) => {
        if (active) setImageError(error.message || `Page ${pageNumber} could not be opened.`);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [slug, pageNumber, shouldRender, nearViewport]);

  return (
    <div className="publication-image-page-shell" ref={shellRef}>
      {!loaded && !imageError && <div className="publication-page-loading">Page {pageNumber}</div>}
      {imageError && <div className="publication-page-loading error">{imageError}</div>}
      {imageUrl && (
        <img
          src={imageUrl}
          alt={`${title} - Page ${pageNumber}`}
          className={loaded ? 'loaded' : ''}
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
};

const ImageFlipPage = forwardRef(({ slug, pageNumber, title, shouldRender }, ref) => (
  <div className="publication-flip-page" ref={ref}>
    <PublicationImage
      slug={slug}
      pageNumber={pageNumber}
      title={title}
      shouldRender={shouldRender}
    />
  </div>
));

const ScrollingImagePage = ({ slug, pageNumber, title }) => {
  const pageRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!pageRef.current || !('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisible(true);
        observer.disconnect();
      },
      { rootMargin: '160px 0px', threshold: 0.08 }
    );
    observer.observe(pageRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      id={`publication-page-${pageNumber}`}
      className={`publication-scroll-page ${visible ? 'is-visible' : ''}`}
      data-page-number={pageNumber}
      ref={pageRef}
    >
      <span className="publication-scroll-page-number">{pageNumber}</span>
      <PublicationImage
        slug={slug}
        pageNumber={pageNumber}
        title={title}
        shouldRender
        lazy
      />
    </div>
  );
};

const PublicationReader = () => {
  const { slug } = useParams();
  const bookRef = useRef(null);
  const readerStageRef = useRef(null);
  const currentPageRef = useRef(0);
  const pageCountRef = useRef(0);
  const bookOrientationRef = useRef('landscape');
  const [publication, setPublication] = useState(null);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageAspectRatio, setPageAspectRatio] = useState(DEFAULT_PAGE_ASPECT_RATIO);
  const [currentPage, setCurrentPage] = useState(0);
  const [renderWindow, setRenderWindow] = useState(() => new Set([0, 1, 2]));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 760);
  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [bookOrientation, setBookOrientation] = useState('landscape');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 760);
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === readerStageRef.current);
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const refreshTimer = window.setTimeout(() => {
      bookRef.current?.pageFlip()?.update();
    }, 80);
    return () => window.clearTimeout(refreshTimer);
  }, [isFullscreen, viewportSize.width, viewportSize.height]);

  useEffect(() => {
    let loadingTask;
    let destroyed = false;

    const loadPublication = async () => {
      try {
        setLoading(true);
        setError('');
        setPublication(null);
        setPdfDocument(null);
        setPageCount(0);
        setPageAspectRatio(DEFAULT_PAGE_ASPECT_RATIO);
        setCurrentPage(0);
        currentPageRef.current = 0;
        pageCountRef.current = 0;
        bookOrientationRef.current = 'landscape';
        setBookOrientation('landscape');
        const response = await publicationsAPI.getBySlug(slug);
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Publication not found.');
        }
        const publicationData = response.data;
        setPublication(publicationData);

        const recordView = () => {
          const viewSessionKey = `veloura-publication-view:${slug}`;
          let shouldRecordView = true;
          try {
            shouldRecordView = !window.sessionStorage.getItem(viewSessionKey);
            if (shouldRecordView) window.sessionStorage.setItem(viewSessionKey, 'pending');
          } catch (storageError) {
            console.warn('Publication view session storage is unavailable:', storageError);
          }
          if (!shouldRecordView) return;

          publicationsAPI.recordView(slug)
            .then(() => {
              try {
                window.sessionStorage.setItem(viewSessionKey, 'recorded');
              } catch {
                // The server-side count has already been recorded.
              }
            })
            .catch((viewError) => {
              try {
                window.sessionStorage.removeItem(viewSessionKey);
              } catch {
                // A later reader visit can retry when storage is available.
              }
              console.error('Failed to record publication view:', viewError);
            });
        };

        if (publicationData.file_type === 'pages') {
          const imagePages = publicationData.pages || [];
          if (imagePages.length === 0) {
            throw new Error('This publication does not contain any page files.');
          }
          const firstPage = imagePages[0];
          const imageAspectRatio = Number(firstPage.width) / Number(firstPage.height);
          if (Number.isFinite(imageAspectRatio) && imageAspectRatio > 0) {
            setPageAspectRatio(imageAspectRatio);
          }
          setPageCount(imagePages.length);
          pageCountRef.current = imagePages.length;
          recordView();
          return;
        }

        loadingTask = pdfjsLib.getDocument(publicationsAPI.getDocumentSource(slug));
        const document = await loadingTask.promise;
        if (destroyed) {
          document.destroy();
          return;
        }
        const firstPage = await document.getPage(1);
        const firstPageViewport = firstPage.getViewport({ scale: 1 });
        const documentAspectRatio = firstPageViewport.width / firstPageViewport.height;
        if (Number.isFinite(documentAspectRatio) && documentAspectRatio > 0) {
          setPageAspectRatio(documentAspectRatio);
        }
        recordView();
        setPdfDocument(document);
        setPageCount(document.numPages);
        pageCountRef.current = document.numPages;
      } catch (loadError) {
        if (!destroyed) {
          console.error('Failed to open publication:', loadError);
          setError(loadError.message || 'This publication could not be opened.');
        }
      } finally {
        if (!destroyed) setLoading(false);
      }
    };

    loadPublication();
    return () => {
      destroyed = true;
      loadingTask?.destroy();
    };
  }, [slug]);

  const pageNumbers = useMemo(
    () => Array.from({ length: pageCount }, (_, index) => index + 1),
    [pageCount]
  );
  const isPagePublication = publication?.file_type === 'pages';
  const isFlipbook = publication?.is_flipbook !== false;
  const usesScrollingReader = isMobile || !isFlipbook;
  const isGaplessScrolling = !isFlipbook && Boolean(publication?.gapless);
  const bookPageWidth = Math.round(BOOK_PAGE_HEIGHT * pageAspectRatio);
  const bookPageMinWidth = Math.round(BOOK_PAGE_MIN_HEIGHT * pageAspectRatio);
  const bookPageMaxWidth = Math.round(BOOK_PAGE_MAX_HEIGHT * pageAspectRatio);
  const bookPageEngineMaxWidth = Math.round(
    BOOK_PAGE_ENGINE_MAX_HEIGHT * pageAspectRatio
  );
  const fullscreenAvailableHeight = Math.max(
    BOOK_PAGE_MIN_HEIGHT,
    viewportSize.height - FULLSCREEN_TOOLBAR_HEIGHT - FULLSCREEN_PADDING
  );
  const fullscreenAvailableWidth = Math.max(
    bookPageMinWidth * 2,
    viewportSize.width - FULLSCREEN_PADDING
  );
  const fullscreenBookWidth = Math.floor(Math.min(
    fullscreenAvailableWidth,
    fullscreenAvailableHeight * pageAspectRatio * 2
  ));
  let bookPositionClass = '';
  if (bookOrientation === 'landscape') {
    if (currentPage === 0) {
      bookPositionClass = 'publication-flipbook--front-centered';
    } else if (pageCount > 1 && currentPage === pageCount - 1) {
      bookPositionClass = 'publication-flipbook--back-centered';
    }
  }

  const updateBookPosition = useCallback((isTurning = false) => {
    const bookElement = readerStageRef.current?.querySelector('.publication-flipbook');
    if (!bookElement) return;
    bookElement.classList.remove(
      'publication-flipbook--front-centered',
      'publication-flipbook--back-centered'
    );
    if (isTurning || bookOrientationRef.current !== 'landscape') return;
    if (currentPageRef.current === 0) {
      bookElement.classList.add('publication-flipbook--front-centered');
    } else if (
      pageCountRef.current > 1
      && currentPageRef.current === pageCountRef.current - 1
    ) {
      bookElement.classList.add('publication-flipbook--back-centered');
    }
  }, []);

  useEffect(() => {
    if (usesScrollingReader || !pdfDocument || pageCount === 0) return undefined;
    const alignmentFrame = window.requestAnimationFrame(() => updateBookPosition());
    return () => window.cancelAnimationFrame(alignmentFrame);
  }, [usesScrollingReader, pdfDocument, pageCount, pageAspectRatio, updateBookPosition]);

  const warmPages = useCallback((index) => {
    currentPageRef.current = index;
    setCurrentPage(index);
    setRenderWindow((current) => {
      const next = new Set(current);
      for (let offset = -3; offset <= 4; offset += 1) {
        const candidate = index + offset;
        if (candidate >= 0 && candidate < pageCount) next.add(candidate);
      }
      return next;
    });
  }, [pageCount]);

  useEffect(() => {
    if (!usesScrollingReader || !pageCount) return undefined;
    const pages = readerStageRef.current?.querySelectorAll('.publication-scroll-page');
    if (!pages?.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!mostVisible) return;
        const pageNumber = Number(mostVisible.target.dataset.pageNumber);
        if (Number.isFinite(pageNumber)) warmPages(pageNumber - 1);
      },
      { threshold: [0.3, 0.55, 0.8] }
    );
    pages.forEach((page) => observer.observe(page));
    return () => observer.disconnect();
  }, [usesScrollingReader, pageCount, warmPages]);

  const nextPage = () => {
    if (usesScrollingReader) {
      document.getElementById(`publication-page-${Math.min(currentPage + 2, pageCount)}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      bookRef.current?.pageFlip()?.flipNext();
    }
  };

  const previousPage = () => {
    if (usesScrollingReader) {
      document.getElementById(`publication-page-${Math.max(currentPage, 1)}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      bookRef.current?.pageFlip()?.flipPrev();
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await readerStageRef.current?.requestFullscreen();
      }
    } catch (fullscreenError) {
      console.error('Fullscreen is unavailable:', fullscreenError);
    }
  };

  const sharePublication = async () => {
    const shareData = {
      title: publication?.title || 'Veloura Publication',
      text: publication?.subtitle || publication?.description || '',
      url: window.location.href
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        window.alert('Publication link copied.');
      }
    } catch (shareError) {
      if (shareError?.name !== 'AbortError') {
        window.prompt('Copy this publication link:', window.location.href);
      }
    }
  };

  const downloadPublication = async () => {
    try {
      setDownloading(true);
      const blob = await publicationsAPI.download(slug);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = isPagePublication
        ? `${slug}-pages.zip`
        : publication?.pdf_original_name || `${slug}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      window.alert(downloadError.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="publication-reader-page"
      style={{
        '--publication-page-ratio': pageAspectRatio,
        '--publication-book-normal-width': `${bookPageMaxWidth * 2}px`,
        '--publication-book-fullscreen-width': `${fullscreenBookWidth}px`
      }}
    >
      <ResponsiveNavbar logo="VELOURA" logoLink="/" scrolled={scrolled} />

      <main>
        {loading && (
          <div className="publication-reader-state">
            <span />
            <h1>Opening Publication</h1>
            <p>Preparing the reader...</p>
          </div>
        )}

        {!loading && error && (
          <div className="publication-reader-state error">
            <span />
            <h1>Publication Unavailable</h1>
            <p>{error}</p>
            <Link to="/publications">Return to publications</Link>
          </div>
        )}

        {!loading && !error && publication && (isPagePublication || pdfDocument) && (
          <>
            <header className="publication-reader-header">
              <Link to="/publications" className="publication-reader-back">
                <span aria-hidden="true">←</span> Publications
              </Link>
              <div className="publication-reader-heading">
                <span>{publication.category_name || 'Veloura Publication'}</span>
                <h1>{publication.title}</h1>
                {(publication.subtitle || publication.issue_label) && (
                  <p>{publication.subtitle || publication.issue_label}</p>
                )}
              </div>
            </header>

            <section
              className={`publication-reader-stage ${usesScrollingReader ? 'publication-reader-stage--scroll' : ''}`.trim()}
              ref={readerStageRef}
            >
              <div className="publication-reader-toolbar">
                <div className="publication-toolbar-group">
                  <button
                    type="button"
                    className="publication-tool-icon"
                    onClick={previousPage}
                    disabled={currentPage <= 0}
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    ←
                  </button>
                  <span className="publication-page-counter">
                    {Math.min(currentPage + 1, pageCount)} / {pageCount}
                  </span>
                  <button
                    type="button"
                    className="publication-tool-icon"
                    onClick={nextPage}
                    disabled={currentPage >= pageCount - 1}
                    aria-label="Next page"
                    title="Next page"
                  >
                    →
                  </button>
                </div>
                <div className="publication-toolbar-group">
                  <button
                    type="button"
                    className="publication-tool-icon"
                    onClick={sharePublication}
                    aria-label="Share publication"
                    title="Share"
                  >
                    ↗
                  </button>
                  {publication.allow_download && (
                    <button
                      type="button"
                      className="publication-tool-text"
                      onClick={downloadPublication}
                      disabled={downloading}
                    >
                      {downloading ? 'Preparing...' : 'Download'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="publication-tool-icon publication-fullscreen-button"
                    onClick={toggleFullscreen}
                    aria-pressed={isFullscreen}
                    aria-label="Toggle fullscreen"
                    title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  >
                    ⛶
                  </button>
                </div>
              </div>

              {usesScrollingReader ? (
                <div className={`publication-scroll-pages ${isGaplessScrolling ? 'publication-scroll-pages--gapless' : ''}`.trim()}>
                  {pageNumbers.map((pageNumber) => (
                    isPagePublication ? (
                      <ScrollingImagePage
                        slug={slug}
                        pageNumber={pageNumber}
                        title={publication.title}
                        key={pageNumber}
                      />
                    ) : (
                      <ScrollingPdfPage
                        document={pdfDocument}
                        pageNumber={pageNumber}
                        key={pageNumber}
                      />
                    )
                  ))}
                </div>
              ) : (
                <Suspense
                  fallback={(
                    <div className="publication-book-area">
                      <div className="publication-flipbook-loading">Preparing flipbook...</div>
                    </div>
                  )}
                >
                  <div className="publication-book-area">
                    <HTMLFlipBook
                      key={`publication-book-${pageAspectRatio.toFixed(5)}`}
                      ref={bookRef}
                      width={bookPageWidth}
                      height={BOOK_PAGE_HEIGHT}
                      size="stretch"
                      minWidth={bookPageMinWidth}
                      maxWidth={bookPageEngineMaxWidth}
                      minHeight={BOOK_PAGE_MIN_HEIGHT}
                      maxHeight={BOOK_PAGE_ENGINE_MAX_HEIGHT}
                      maxShadowOpacity={0.42}
                      showCover
                      usePortrait
                      mobileScrollSupport
                      drawShadow
                      flippingTime={850}
                      className={`publication-flipbook ${bookPositionClass}`.trim()}
                      onFlip={(event) => warmPages(event.data)}
                      onChangeState={(event) => updateBookPosition(
                        event.data === 'user_fold' || event.data === 'flipping'
                      )}
                      onChangeOrientation={(event) => {
                        bookOrientationRef.current = event.data;
                        setBookOrientation(event.data);
                        updateBookPosition();
                      }}
                      onInit={(event) => {
                        bookOrientationRef.current = event.data.mode;
                        setBookOrientation(event.data.mode);
                        updateBookPosition();
                      }}
                    >
                      {pageNumbers.map((pageNumber, index) => (
                        isPagePublication ? (
                          <ImageFlipPage
                            slug={slug}
                            pageNumber={pageNumber}
                            title={publication.title}
                            shouldRender={renderWindow.has(index)}
                            key={pageNumber}
                          />
                        ) : (
                          <FlipPage
                            document={pdfDocument}
                            pageNumber={pageNumber}
                            shouldRender={renderWindow.has(index)}
                            key={pageNumber}
                          />
                        )
                      ))}
                    </HTMLFlipBook>
                  </div>
                </Suspense>
              )}
            </section>

            {(publication.description || publication.issue_label) && (
              <section className="publication-reader-notes">
                <span className="publication-reader-rule" />
                <div>
                  <span>{publication.issue_label || 'About this release'}</span>
                  {publication.description && <p>{publication.description}</p>}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {!isFullscreen && <Footer />}
    </div>
  );
};

export default PublicationReader;
